/**
 * MilestonePredictions - AI-smart predictive milestone cards
 * Pro-only feature showing personalized travel goals
 */
import { useState, useEffect } from "react";
import { 
  Trophy, Globe, Flame, TrendingUp, Target, 
  Lock, Sparkles, ChevronRight, MapPin 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { API } from "@/App";
import { useEntitlements } from "@/hooks/useEntitlements";
import { PaywallScreen, PaywallType } from "@/components/PaywallScreen";

const iconMap = {
  trophy: Trophy,
  globe: Globe,
  flame: Flame,
  "trending-up": TrendingUp,
  earth: Globe,
  "map-pin": MapPin,
};

const MilestonePredictions = () => {
  const { hasUniversalAccess, hasPaidAccess } = useEntitlements();
  const hasAccess = hasUniversalAccess || hasPaidAccess;
  
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [showPaywall, setShowPaywall] = useState(false);
  
  useEffect(() => {
    fetchMilestones();
  }, []);
  
  const fetchMilestones = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/premium/milestones`);
      setData(response.data);
    } catch (error) {
      console.error("Failed to fetch milestones:", error);
    } finally {
      setLoading(false);
    }
  };
  
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 1: return "from-coral to-coral/60";
      case 2: return "from-emerald-500 to-emerald-500/60";
      case 3: return "from-amber-500 to-amber-500/60";
      default: return "from-white/20 to-white/10";
    }
  };
  
  if (loading) {
    return (
      <Card className="bg-charcoal/95 border-coral/20">
        <CardContent className="p-6">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-coral"></div>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <>
      <Card className="bg-charcoal/95 border-coral/20 backdrop-blur-xl">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-white">
              <Target className="w-5 h-5 text-coral" />
              Next Milestones
            </CardTitle>
            {!hasAccess && (
              <Badge variant="outline" className="border-coral/50 text-coral text-xs">
                <Lock className="w-3 h-3 mr-1" />
                Pro
              </Badge>
            )}
          </div>
        </CardHeader>
        
        <CardContent className="space-y-3">
          {/* Current Stats Summary */}
          {data?.stats && (
            <div className="flex items-center justify-between bg-white/5 rounded-lg p-3 mb-4">
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-white">{data.stats.countries}</p>
                <p className="text-xs text-white/60">Countries</p>
              </div>
              <div className="w-px h-8 bg-white/10"></div>
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-coral">{data.stats.world_percentage}%</p>
                <p className="text-xs text-white/60">World</p>
              </div>
              <div className="w-px h-8 bg-white/10"></div>
              <div className="text-center flex-1">
                <p className="text-lg font-bold text-emerald-400">{data.stats.current_rank}</p>
                <p className="text-xs text-white/60">Rank</p>
              </div>
            </div>
          )}
          
          {/* Prediction Cards */}
          {hasAccess && data?.predictions?.length > 0 && (
            <div className="space-y-3">
              {data.predictions.slice(0, 4).map((prediction, idx) => {
                const IconComponent = iconMap[prediction.icon] || Target;
                
                return (
                  <div 
                    key={idx}
                    className={`relative overflow-hidden rounded-lg p-4 bg-gradient-to-r ${getPriorityColor(prediction.priority)}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-white/20 rounded-lg">
                        <IconComponent className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm">{prediction.title}</p>
                        <p className="text-xs text-white/70 mt-0.5">{prediction.description}</p>
                        
                        {/* Progress bar if available */}
                        {prediction.progress && (
                          <div className="mt-2">
                            <Progress 
                              value={prediction.progress.percentage} 
                              className="h-1.5 bg-white/20"
                            />
                            <p className="text-xs text-white/60 mt-1">
                              {prediction.progress.current} / {prediction.progress.target}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          
          {/* Teaser for non-premium */}
          {!hasAccess && data?.teaser && (
            <div className="space-y-3">
              {/* Teaser info */}
              <div className="text-center py-2">
                <Sparkles className="w-8 h-8 text-coral mx-auto mb-2" />
                <p className="text-white font-medium mb-1">
                  {data.teaser.predictions_count} predictions ready
                </p>
                <p className="text-xs text-white/60">{data.teaser.message}</p>
              </div>
              
              <Button
                onClick={() => setShowPaywall(true)}
                className="w-full bg-coral hover:bg-coral/90"
              >
                <Lock className="w-4 h-4 mr-2" />
                Learn more on duolyst.co.uk
              </Button>
            </div>
          )}
          
          {/* Empty state */}
          {hasAccess && (!data?.predictions || data.predictions.length === 0) && (
            <div className="text-center py-6">
              <Globe className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <p className="text-white/60 text-sm">Start exploring to unlock predictions!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {showPaywall && (
        <PaywallScreen
          type={PaywallType.MILESTONE_PREDICTIONS}
          isOpen={showPaywall}
          onClose={() => setShowPaywall(false)}
          zIndex={10002}
        />
      )}
    </>
  );
};

export default MilestonePredictions;
